import React, { useCallback, useMemo, useRef, useState } from "react";
import { Form } from "react-final-form";
import { useSelector } from "react-redux";
import { Popover } from "@blueprintjs/core";
import { MultiSlider } from "@blueprintjs/core";
import classNames from "classnames";
import { roundToFactor, getNumberFromNumberString } from "utils/string";
import { isFormValid } from "shared/validators";
import { useOnScrollPopoverStateListener } from "hooks/useScrollDebounceListener";
import NumberInput from "rs-components/Input/components/NumberInput";
import Button, { ButtonSize, ButtonStyle } from "components/Button";
import RangeValue from "rs-components/RangeValue";
import { scrollElementSelector } from "store/global/global.selectors";
import ChevronDown from "icons/rs_chevron_down.svg";
import * as styles from "./styles.module.scss";
import * as colors from "styles/colorValues.module.scss";
const GRADIENT_ID = "CONFIDENCE_MAP_GRADIENT";
var FormFields;
(function (FormFields) {
    FormFields["MIN"] = "min";
    FormFields["MAX"] = "max";
})(FormFields || (FormFields = {}));
const ConfidenceMap = ({ source, className, scrollElement, spaceviewerUid, changeConfidence }) => {
    const sliderRef = useRef(null);
    const elementScroll = useSelector(scrollElementSelector(scrollElement));
    const [dialogIsOpen, setDialogIsOpen] = useState(false);
    const [popoverRef, setPopoverRef] = useState(null);
    const [lastValidSliderValues, setLastValidSliderValues] = useState({
        [FormFields.MIN]: 0,
        [FormFields.MAX]: 0,
    });
    const confidence = useMemo(() => ({ [FormFields.MIN]: source?.confidenceMin ?? 0, [FormFields.MAX]: source?.confidenceMax ?? 100 }), [source?.confidenceMax, source?.confidenceMin]);
    const isFullRange = useMemo(() => confidence[FormFields.MIN] === 0 && confidence[FormFields.MAX] === 100, [confidence]);
    const handleUpdateConfidence = useCallback((values) => {
        changeConfidence && changeConfidence(values);
        setDialogIsOpen(false);
    }, [changeConfidence]);
    const dialog = useMemo(() => (React.createElement(Form, { onSubmit: handleUpdateConfidence, initialValues: confidence, validateOnBlur: true, render: ({ form, handleSubmit, ...renderProps }) => {
            const handleChangeSlider = ([min, max]) => {
                const roundMinValue = roundToFactor(getNumberFromNumberString(min));
                const roundMaxValue = roundToFactor(getNumberFromNumberString(max));
                lastValidSliderValues[FormFields.MIN] = roundMinValue;
                lastValidSliderValues[FormFields.MAX] = roundMaxValue;
                form.batch(() => {
                    form.change(FormFields.MIN, roundMinValue);
                    form.change(FormFields.MAX, roundMaxValue);
                    if (Object.keys(renderProps.errors ?? {}).length) {
                        setTimeout(() => {
                            form.blur(FormFields.MIN);
                            form.blur(FormFields.MAX);
                        }, 0);
                    }
                });
            };
            const handleInputBlur = (input) => {
                if (isFormValid(form.getState())) {
                    const newValues = {
                        ...lastValidSliderValues,
                        [input.name]: roundToFactor(getNumberFromNumberString(input.value)),
                    };
                    handleChangeSlider([newValues[FormFields.MIN], newValues[FormFields.MAX]]);
                }
            };
            const baseInputProps = {
                component: NumberInput,
                unit: "%",
                restrictions: { IsNumber: true, MinValueEqual: 0, MaxValueEqual: 100 },
                onBlur: handleInputBlur,
            };
            return (React.createElement("div", { className: styles.dialog },
                React.createElement("h4", { className: styles["dialog-title"] }, "Confidence"),
                React.createElement(RangeValue, { from: {
                        ...baseInputProps,
                        name: FormFields.MIN,
                        label: "Min",
                    }, to: {
                        ...baseInputProps,
                        name: FormFields.MAX,
                        label: "Max",
                    }, canBeEqual: true, editable: true, className: styles["dialog-inputs"], suppressDash: true }),
                React.createElement(MultiSlider, { ref: sliderRef, className: styles.multiSlider, stepSize: 0.5, max: 100, min: 0, labelStepSize: 25, labelRenderer: (value) => React.createElement("span", null, value), onChange: handleChangeSlider, onRelease: handleChangeSlider },
                    React.createElement(MultiSlider.Handle, { type: "start", value: roundToFactor(lastValidSliderValues[FormFields.MIN]) }),
                    React.createElement(MultiSlider.Handle, { type: "end", value: roundToFactor(lastValidSliderValues[FormFields.MAX]) })),
                React.createElement("div", { className: styles["dialog-buttons"] },
                    React.createElement(Button, { onClick: () => setDialogIsOpen(false), label: "cancel", style: ButtonStyle.Secondary, size: ButtonSize.Big }),
                    React.createElement(Button, { onClick: () => (isFormValid(renderProps) ? handleSubmit() : setDialogIsOpen(false)), label: "ok", style: ButtonStyle.Primary, size: ButtonSize.Big }))));
        } })), [handleUpdateConfidence, confidence, lastValidSliderValues]);
    const renderGradient = useMemo(() => {
        const background = isFullRange ? colors.s100 : colors.s800;
        const selectedRangeBackground = isFullRange ? colors.s200 : colors.p500;
        return (React.createElement("linearGradient", { id: `${GRADIENT_ID}_${spaceviewerUid}` },
            React.createElement("stop", { offset: "0%", stopColor: background }),
            React.createElement("stop", { offset: `${confidence[FormFields.MIN]}%`, stopColor: background }),
            React.createElement("stop", { offset: `${confidence[FormFields.MIN]}%`, stopColor: selectedRangeBackground }),
            React.createElement("stop", { offset: `${confidence[FormFields.MAX]}%`, stopColor: selectedRangeBackground }),
            React.createElement("stop", { offset: `${confidence[FormFields.MAX]}%`, stopColor: background }),
            React.createElement("stop", { offset: "100%", stopColor: background })));
    }, [isFullRange, spaceviewerUid, confidence]);
    const confidenceBar = useMemo(() => {
        const confidenceColor = isFullRange ? colors.s500 : colors.s700;
        return (React.createElement("svg", { className: styles.confidenceBar, viewBox: "0 0 287 40", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
            React.createElement("path", { d: "M4 32L3.5 40H269V0.5L4 32Z", fill: `url(#${GRADIENT_ID}_${spaceviewerUid})` }),
            React.createElement("rect", { y: "32", width: "8", height: "8", rx: "4", fill: confidenceColor }),
            React.createElement("rect", { x: "119", y: "17", width: "23", height: "23", rx: "11.5", fill: confidenceColor }),
            React.createElement("rect", { x: "247", width: "40", height: "40", rx: "20", fill: confidenceColor }),
            React.createElement("defs", null, renderGradient)));
    }, [isFullRange, renderGradient, spaceviewerUid]);
    useOnScrollPopoverStateListener(dialogIsOpen, setDialogIsOpen, popoverRef, elementScroll);
    return (React.createElement("div", { className: classNames(styles.root, className) },
        React.createElement(Popover, { popoverRef: setPopoverRef, isOpen: dialogIsOpen, onInteraction: setDialogIsOpen, content: dialog, placement: "bottom-start", modifiers: { offset: { options: { offset: [5, 40] } } }, onOpening: () => setLastValidSliderValues({
                [FormFields.MIN]: confidence.min ?? 0,
                [FormFields.MAX]: confidence.max ?? 0,
            }), renderTarget: (props) => {
                const { className: popoverClassName, isOpen, ...restProps } = props;
                return (React.createElement("div", { ...restProps, className: classNames(styles.barWrapper, popoverClassName) },
                    React.createElement("p", { className: styles.label },
                        "Confidence",
                        " ",
                        React.createElement("span", { className: styles["label-value"] },
                            "(",
                            isFullRange ? "All" : `${confidence[FormFields.MIN]}% - ${confidence[FormFields.MAX]}%`,
                            ")"),
                        " ",
                        React.createElement(ChevronDown, { className: classNames(styles["label-chevron"], {
                                [styles["label-chevron-active"]]: isOpen,
                            }) })),
                    confidenceBar));
            } }),
        React.createElement("div", { className: styles.limits },
            React.createElement("p", { className: classNames(styles.limit, styles["limit-low"]) }, "Low"),
            React.createElement("p", { className: classNames(styles.limit, styles["limit-medium"]) }, "Medium"),
            React.createElement("p", { className: classNames(styles.limit, styles["limit-high"]) }, "High"))));
};
export default ConfidenceMap;
